import { useTheme } from '../context/ThemeContext';
import { GOAL_LABELS } from '../utils/dynamics';
import { Users, TrendingUp, AlertCircle, Calendar } from 'lucide-react';

function StatBox({ icon: Icon, label, value, color, isDark }) {
  const bg  = isDark ? '#1A1526' : '#FFFFFF';
  const bdr = isDark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.06)';
  const txt = isDark ? '#F2EEF9' : '#2D1B35';
  const mut = isDark ? 'rgba(200,185,220,.55)' : 'rgba(80,60,100,.5)';
  return (
    <div style={{ background:bg, border:`1px solid ${bdr}`, borderRadius:14, padding:'14px 16px',
                  display:'flex', alignItems:'center', gap:12,
                  boxShadow:isDark?'0 2px 12px rgba(0,0,0,.3)':'0 2px 12px rgba(100,60,140,.07)' }}>
      <div style={{ width:42, height:42, borderRadius:12, display:'flex', alignItems:'center',
                    justifyContent:'center', flexShrink:0, ...color }}>
        <Icon size={20}/>
      </div>
      <div>
        <p style={{ color:txt, fontFamily:'Nunito,system-ui', fontSize:'1.6rem', fontWeight:800, margin:0, lineHeight:1 }}>{value}</p>
        <p style={{ color:mut, fontSize:'.72rem', margin:'3px 0 0' }}>{label}</p>
      </div>
    </div>
  );
}

export default function AdminDashboard({ clients = [], onSelect }) {
  const { isDark, theme } = useTheme();
  const acc = theme.accent;
  const txt = isDark ? '#F2EEF9' : '#2D1B35';
  const mut = isDark ? 'rgba(200,185,220,.55)' : 'rgba(80,60,100,.5)';
  const bg  = isDark ? '#1A1526' : '#FFFFFF';
  const bdr = isDark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.06)';

  const now         = new Date();
  const twoWeeksAgo = new Date(now - 14*24*60*60*1000);
  const oneWeekAgo  = new Date(now -  7*24*60*60*1000);

  const active           = clients.filter(c => c.is_active);
  const needAttention    = active.filter(c => !c.last_measurement_date || new Date(c.last_measurement_date) < twoWeeksAgo);
  const measuredThisWeek = active.filter(c => c.last_measurement_date && new Date(c.last_measurement_date) >= oneWeekAgo);
  const byGoal           = Object.entries(GOAL_LABELS)
    .map(([key, label]) => ({ key, label, count: active.filter(c => c.goal === key).length }))
    .filter(g => g.count > 0);

  const ClientRow = ({ client, color, icon, badge }) => {
    const name = [client.first_name, client.last_name].filter(Boolean).join(' ') || client.username;
    return (
      <div onClick={() => onSelect && onSelect(client.id)}
        style={{ background:bg, border:`1px solid ${bdr}`, borderRadius:12, padding:'12px 14px',
                 cursor:'pointer', display:'flex', alignItems:'center', gap:12,
                 transition:'all .15s', marginBottom:8 }}
        onMouseEnter={e => e.currentTarget.style.borderColor = acc}
        onMouseLeave={e => e.currentTarget.style.borderColor = bdr}>
        <div style={{ width:36, height:36, borderRadius:'50%', flexShrink:0,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      background: color+'20', fontSize:'.875rem', fontWeight:700, color }}>
          {name[0]}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <p style={{ color:txt, fontSize:'.85rem', fontWeight:600, margin:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{name}</p>
          <p style={{ color:mut, fontSize:'.72rem', margin:'2px 0 0' }}>
            {GOAL_LABELS[client.goal] || '—'}
            {client.last_weight ? ` · ${parseFloat(client.last_weight)} кг` : ''}
          </p>
        </div>
        <span style={{ color, fontSize:'.72rem', fontWeight:600, flexShrink:0 }}>{badge}</span>
      </div>
    );
  };

  return (
    <div>
      <h1 style={{ color:txt, fontFamily:'Nunito,system-ui', fontWeight:800, fontSize:'1.5rem',
                   letterSpacing:-.5, margin:'0 0 18px' }}>Обзор клиентов</h1>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:10, marginBottom:24 }}>
        <StatBox icon={Users}       label="Всего клиентов"       value={active.length}            isDark={isDark} color={{ background:'rgba(155,111,212,.15)', color:'#9B6FD4' }}/>
        <StatBox icon={TrendingUp}  label="Замер на этой неделе" value={measuredThisWeek.length}  isDark={isDark} color={{ background:'rgba(34,197,94,.15)',   color:'#22C55E' }}/>
        <StatBox icon={AlertCircle} label="Нет замера 2+ нед."   value={needAttention.length}     isDark={isDark} color={{ background:'rgba(245,158,11,.15)', color:'#F59E0B' }}/>
        <StatBox icon={Calendar}    label="Типов целей"          value={byGoal.length}            isDark={isDark} color={{ background:`${acc}20`,              color:acc }}/>
      </div>

      {/* Need attention */}
      {needAttention.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <p style={{ color:mut, fontSize:'.68rem', fontWeight:700, textTransform:'uppercase',
                      letterSpacing:'.08em', margin:'0 0 10px' }}>⚠️ Нет замера более 2 недель</p>
          {needAttention.map(c => {
            const last = c.last_measurement_date
              ? new Date(c.last_measurement_date).toLocaleDateString('ru-RU',{day:'numeric',month:'short'})
              : 'Никогда';
            return <ClientRow key={c.id} client={c} color="#F59E0B" badge={`Последний: ${last}`}/>;
          })}
        </div>
      )}

      {/* Goals */}
      {byGoal.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <p style={{ color:mut, fontSize:'.68rem', fontWeight:700, textTransform:'uppercase',
                      letterSpacing:'.08em', margin:'0 0 10px' }}>По целям</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
            {byGoal.map(g => (
              <div key={g.key} style={{ background:bg, border:`1px solid ${bdr}`, borderRadius:12,
                                        padding:'12px', textAlign:'center' }}>
                <p style={{ color:txt, fontFamily:'Nunito,system-ui', fontSize:'1.5rem', fontWeight:800, margin:'0 0 4px' }}>{g.count}</p>
                <p style={{ color:mut, fontSize:'.72rem', margin:0 }}>{g.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active this week */}
      {measuredThisWeek.length > 0 && (
        <div>
          <p style={{ color:mut, fontSize:'.68rem', fontWeight:700, textTransform:'uppercase',
                      letterSpacing:'.08em', margin:'0 0 10px' }}>✅ Замерились на этой неделе</p>
          {measuredThisWeek.map(c => {
            const date = new Date(c.last_measurement_date).toLocaleDateString('ru-RU',{day:'numeric',month:'short'});
            return <ClientRow key={c.id} client={c} color="#22C55E" badge={date}/>;
          })}
        </div>
      )}

      {active.length === 0 && (
        <div style={{ textAlign:'center', padding:'3rem', color:mut }}>
          <p style={{ fontSize:'2rem', margin:'0 0 8px' }}>👥</p>
          <p>Клиентов пока нет</p>
        </div>
      )}
    </div>
  );
}
